import {
  TARGET_INVESTOR_NEXT_ACTION_OPTIONS,
  TargetInvestorNextAction,
  TargetInvestorRelationship,
  TargetInvestorSourceOption,
  TargetInvestorSourceSelection,
} from '../types/targetInvestor.types';

export const TARGET_INVESTOR_FIELD_FILTER_ALL = 'all';
export const TARGET_INVESTOR_FIELD_FILTER_NOT_SET = 'not_set';

const NEXT_ACTION_LABELS = new Map<TargetInvestorNextAction, string>(
  TARGET_INVESTOR_NEXT_ACTION_OPTIONS.map((option) => [option.value, option.label])
);
const NEXT_ACTION_ORDER = new Map<TargetInvestorNextAction, number>(
  TARGET_INVESTOR_NEXT_ACTION_OPTIONS.map((option, index) => [option.value, index])
);

const RELATIONSHIP_ORDER: Record<TargetInvestorRelationship['kind'], number> = {
  client: 0,
  volocean: 1,
  syndicate: 2,
  unassigned: 3,
};

export const abbreviateSourceLabel = (label: string | null | undefined): string => {
  if (!label) return '';
  const words = label.trim().split(/\s+/).filter(Boolean);
  if (words.length <= 2) return label.trim();
  return words
    .map((word) => word.replace(/[^A-Za-z0-9]/g, '').charAt(0).toUpperCase())
    .filter(Boolean)
    .join('');
};

export const normalizeTargetInvestorSourceLabel = (label: string): string => label.trim();

export const normalizeTargetInvestorSourceLabelKey = (label: string): string =>
  normalizeTargetInvestorSourceLabel(label).toLowerCase();

export const isTargetInvestorNextAction = (value: unknown): value is TargetInvestorNextAction =>
  typeof value === 'string' && NEXT_ACTION_LABELS.has(value as TargetInvestorNextAction);

export const normalizeTargetInvestorNextAction = (value: unknown): TargetInvestorNextAction | null =>
  isTargetInvestorNextAction(value) ? value : null;

export const formatTargetInvestorNextAction = (
  value: TargetInvestorNextAction | null | undefined
): string => (value ? NEXT_ACTION_LABELS.get(value) || '' : '');

export const normalizeTargetInvestorRelationship = (
  value: TargetInvestorRelationship | null | undefined
): TargetInvestorRelationship => {
  if (!value || !value.kind) {
    return { kind: 'unassigned' };
  }

  if (value.kind === 'client' || value.kind === 'volocean' || value.kind === 'unassigned') {
    return { kind: value.kind };
  }

  const syndicateId = value.syndicateId?.trim();
  const label = value.label?.trim();
  if (value.kind === 'syndicate' && syndicateId && label) {
    return { kind: 'syndicate', syndicateId, label };
  }

  return { kind: 'unassigned' };
};

export const formatTargetInvestorRelationship = (
  value: TargetInvestorRelationship | null | undefined
): string => {
  const relationship = normalizeTargetInvestorRelationship(value);
  if (relationship.kind === 'client') return 'Client';
  if (relationship.kind === 'volocean') return 'Volocean';
  if (relationship.kind === 'syndicate') return relationship.label || 'Syndicate';
  return 'Unassigned';
};

export const resolveTargetInvestorSourceLabel = (
  selection: TargetInvestorSourceSelection | null | undefined,
  options: TargetInvestorSourceOption[]
): string => {
  if (!selection) return '';
  const currentOption = options.find((option) => option.id === selection.optionId);
  return normalizeTargetInvestorSourceLabel(currentOption?.label || selection.labelSnapshot || '');
};

const compareTextBlankLast = (left: string, right: string): number => {
  if (!left && !right) return 0;
  if (!left) return 1;
  if (!right) return -1;
  return left.localeCompare(right, undefined, { sensitivity: 'base' });
};

export const compareTargetInvestorSources = (
  left: TargetInvestorSourceSelection | null | undefined,
  right: TargetInvestorSourceSelection | null | undefined,
  options: TargetInvestorSourceOption[]
): number =>
  compareTextBlankLast(
    resolveTargetInvestorSourceLabel(left, options),
    resolveTargetInvestorSourceLabel(right, options)
  );

export const compareTargetInvestorRelationships = (
  left: TargetInvestorRelationship | null | undefined,
  right: TargetInvestorRelationship | null | undefined
): number => {
  const normalizedLeft = normalizeTargetInvestorRelationship(left);
  const normalizedRight = normalizeTargetInvestorRelationship(right);
  const rankDifference =
    RELATIONSHIP_ORDER[normalizedLeft.kind] - RELATIONSHIP_ORDER[normalizedRight.kind];
  if (rankDifference !== 0) return rankDifference;
  return compareTextBlankLast(
    formatTargetInvestorRelationship(normalizedLeft),
    formatTargetInvestorRelationship(normalizedRight)
  );
};

export const compareTargetInvestorNextActions = (
  left: TargetInvestorNextAction | null | undefined,
  right: TargetInvestorNextAction | null | undefined
): number => {
  if (!left && !right) return 0;
  if (!left) return 1;
  if (!right) return -1;
  return (NEXT_ACTION_ORDER.get(left) ?? Number.MAX_SAFE_INTEGER) -
    (NEXT_ACTION_ORDER.get(right) ?? Number.MAX_SAFE_INTEGER);
};

export const matchesTargetInvestorSourceFilter = (
  selection: TargetInvestorSourceSelection | null | undefined,
  filter: string
): boolean => {
  if (!filter || filter === TARGET_INVESTOR_FIELD_FILTER_ALL) return true;
  if (filter === TARGET_INVESTOR_FIELD_FILTER_NOT_SET) return !selection?.optionId;
  return selection?.optionId === filter;
};

export const matchesTargetInvestorRelationshipFilter = (
  value: TargetInvestorRelationship | null | undefined,
  filter: string
): boolean => {
  if (!filter || filter === TARGET_INVESTOR_FIELD_FILTER_ALL) return true;
  return normalizeTargetInvestorRelationship(value).kind === filter;
};

export const matchesTargetInvestorNextActionFilter = (
  value: TargetInvestorNextAction | null | undefined,
  filter: string
): boolean => {
  if (!filter || filter === TARGET_INVESTOR_FIELD_FILTER_ALL) return true;
  if (filter === TARGET_INVESTOR_FIELD_FILTER_NOT_SET) return !value;
  return value === filter;
};

export const getTargetInvestorFieldExportValues = (
  target: {
    introducedBySource?: TargetInvestorSourceSelection | null;
    relationship?: TargetInvestorRelationship | null;
    nextAction?: TargetInvestorNextAction | null;
  },
  options: TargetInvestorSourceOption[]
): {
  introducedBySource: string;
  relationship: string;
  nextAction: string;
} => ({
  introducedBySource: resolveTargetInvestorSourceLabel(target.introducedBySource, options),
  relationship: formatTargetInvestorRelationship(target.relationship),
  nextAction: formatTargetInvestorNextAction(target.nextAction),
});

export const isSyndicationTargetWorkspace = (workspaceId: string): boolean =>
  workspaceId.startsWith('syn_');
